import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Newspaper, 
  BookOpen, 
  Users, 
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsList = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/news?published_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setNewsList(await response.json());
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Erreur lors du chargement des actualités');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setNewsList(newsList.filter(n => n.id !== id));
        toast.success('Actualité supprimée');
        setDeleteId(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (news) => {
    try {
      const response = await fetch(`${API_URL}/api/news/${news.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !news.published })
      });
      if (response.ok) {
        const updated = await response.json();
        setNewsList(newsList.map(n => n.id === news.id ? updated : n));
        toast.success(updated.published ? 'Actualité publiée' : 'Actualité dépubliée');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news', active: true },
    { icon: BookOpen, label: 'Livres', path: '/admin/books' }
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                    item.active 
                      ? 'bg-hermes-600 text-white' 
                      : 'text-stone-300 hover:bg-stone-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 px-4 py-2 text-stone-300">
            <Users className="w-5 h-5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.full_name || admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-serif font-bold text-stone-900">Actualités</h2>
            <p className="text-stone-600 mt-1">Gérez les actualités de votre site</p>
          </div>
          <Link
            to="/admin/news/new"
            className="flex items-center gap-2 px-4 py-2 bg-hermes-600 text-white hover:bg-hermes-700 transition-colors"
            data-testid="add-news-button"
          >
            <Plus className="w-5 h-5" />
            Nouvelle actualité
          </Link>
        </div>

        {/* News List */}
        <div className="bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Titre</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Catégorie</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Statut</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    Chargement...
                  </td>
                </tr>
              ) : newsList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    Aucune actualité pour le moment
                  </td>
                </tr>
              ) : (
                newsList.map((news) => (
                  <tr key={news.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-stone-900">{news.title}</p>
                    </td>
                    <td className="px-6 py-4 text-stone-600">{news.category}</td>
                    <td className="px-6 py-4 text-stone-600">
                      {new Date(news.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        news.published 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {news.published ? 'Publié' : 'Brouillon'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => togglePublish(news)}
                          className="p-2 text-stone-500 hover:text-hermes-600 transition-colors"
                          title={news.published ? 'Dépublier' : 'Publier'}
                          data-testid={`toggle-publish-${news.id}`}
                        >
                          {news.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <Link
                          to={`/admin/news/${news.id}`}
                          className="p-2 text-stone-500 hover:text-hermes-600 transition-colors"
                          title="Modifier"
                          data-testid={`edit-news-${news.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteId(news.id)}
                          className="p-2 text-stone-500 hover:text-red-600 transition-colors"
                          title="Supprimer"
                          data-testid={`delete-news-${news.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-lg font-medium text-stone-900 mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-stone-600 mb-6">
                Êtes-vous sûr de vouloir supprimer cette actualité ? Cette action est irréversible.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-stone-600 hover:text-stone-900 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(deleteId)}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
                  data-testid="confirm-delete-button"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminNewsList;
